import {
  Combobox as HuiCombobox,
  ComboboxButton as HuiComboboxButton,
  ComboboxInput as HuiComboboxInput,
  ComboboxOption as HuiComboboxOption,
  ComboboxOptions as HuiComboboxOptions,
} from '@headlessui/react';
import { useCallback, useEffect, useState } from 'react';
import { BeatLoader, PulseLoader } from 'react-spinners';
import RightChevronIcon from '@/assets/chevron-right.svg';
import SearchIcon from '@/assets/search.svg';
import { getColors } from '@/tailwind/get-colors';
import { cx } from '@/utils/cx';
import { simulateMouseClick } from '@/utils/dom-events/simulate-mouse-click';
import { ProfileCircle } from './profile-circle';
import { Button } from '../ui/button';

type ResidentSearchComboboxProps<TOption extends object> = {
  isLoading: boolean;
  options: TOption[];
  placeholder?: string;
  query: string;
  queryDebounceMs: number;
  selectedOptions: TOption[];
  displayExtractor: (option: TOption) => string;
  identifierExtractor: (option: TOption) => string;
  keyExtractor: (option: TOption) => number | string;
  queryExtractor: (option: TOption) => string;
  onQueryChange: (query: string) => void;
  onSelect: (value: TOption) => void;
};

export const ResidentSearchCombobox = <TOption extends object>({
  isLoading,
  options,
  placeholder = 'Search',
  query,
  queryDebounceMs,
  selectedOptions,
  displayExtractor,
  identifierExtractor,
  keyExtractor,
  // queryExtractor,
  onQueryChange,
  onSelect,
}: ResidentSearchComboboxProps<TOption>): JSX.Element => {
  const [inputQueryValue, setInputQueryValue] = useState('');
  const [hoveredOption, setHoveredOption] = useState<TOption | null>(null);
  // probably don't need to filter the results unless fetching more
  const filteredOptions = options;
  // const filteredOptions = useMemo(
  //   () =>
  //     query === ''
  //       ? options
  //       : options.filter((option) =>
  //           queryExtractor(option).toLowerCase().includes(query.toLowerCase()),
  //         ),
  //   [options, query, queryExtractor],
  // );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (inputQueryValue !== query) {
        onQueryChange(inputQueryValue);
      }
    }, queryDebounceMs);

    return () => clearTimeout(delayDebounceFn);
  }, [inputQueryValue, query, queryDebounceMs, onQueryChange]);

  const isTyping = query !== inputQueryValue;

  const isOptionAdded = useCallback(
    (option: TOption) =>
      selectedOptions.findIndex(
        (opt) => keyExtractor(opt) === keyExtractor(option),
      ) > -1,
    [selectedOptions, keyExtractor],
  );

  const shouldHideResults = isTyping || isLoading;

  return (
    <HuiCombobox<TOption>
      immediate={true}
      onChange={(option) => {
        if (option) {
          onSelect(option);
        }
      }}
      onClose={() => {
        setHoveredOption(null);
      }}
    >
      {({ activeOption }) => {
        const highlightedOption = activeOption || hoveredOption;
        return (
          <>
            <div className="relative">
              <HuiComboboxInput<TOption | null>
                className={cx(
                  'focusable',
                  'w-full rounded-xl border border-gray-300 p-4 pl-10 text-sm/4',
                  'data-open:rounded-b-none data-open:border-b-0',
                )}
                displayValue={(option) =>
                  option ? displayExtractor(option) : ''
                }
                onChange={(event) => setInputQueryValue(event.target.value)}
                placeholder={placeholder}
                value={inputQueryValue}
              />
              <HuiComboboxButton className="absolute inset-y-0 left-0 pr-2 pl-4">
                <SearchIcon className="size-4 text-gray-400" />
              </HuiComboboxButton>
            </div>

            <HuiComboboxOptions
              transition
              anchor="bottom"
              className={cx(
                'min-h-20 rounded-b-xl border border-gray-300 border-t-gray-100 bg-white',
                'w-(--input-width)',
                'transition duration-100 ease-in data-leave:data-closed:opacity-0',
              )}
            >
              <div className="flex h-72 flex-row">
                <div className="relative w-1/2 overflow-auto p-4">
                  {filteredOptions.length === 0 && query.length > 0 ? (
                    <div className="pt-2 text-sm text-gray-700 italic">
                      No residents found
                    </div>
                  ) : (
                    filteredOptions.map((option) => {
                      const shouldHighlight =
                        highlightedOption &&
                        keyExtractor(highlightedOption) ===
                          keyExtractor(option);
                      return (
                        <HuiComboboxOption
                          className={cx(
                            'flex cursor-default items-center gap-2 rounded-md p-2 select-none',
                            shouldHighlight && 'bg-gray-100',
                          )}
                          key={keyExtractor(option)}
                          onMouseDown={(event) => {
                            // prevents the dropdown being closed when an option is selected
                            event.preventDefault();
                          }}
                          onMouseEnter={() => setHoveredOption(option)}
                          value={option}
                        >
                          <div className="flex w-full flex-row items-center gap-3 text-sm">
                            <ProfileCircle
                              className={cx(
                                isOptionAdded(option) && 'bg-primary',
                              )}
                              size="sm"
                            />
                            {displayExtractor(option)}
                            <div className="grow"></div>
                            <div
                              className={cx(
                                'size-5 min-w-5 text-gray-400 opacity-0',
                                shouldHighlight && 'opacity-100',
                              )}
                            >
                              <RightChevronIcon />
                            </div>
                          </div>
                        </HuiComboboxOption>
                      );
                    })
                  )}
                  <div
                    className={cx(
                      'absolute top-0 right-0 bottom-0 left-0 h-0 bg-white opacity-0 transition-all duration-300',
                      shouldHideResults && 'bottom-0 h-auto opacity-100',
                    )}
                  >
                    {shouldHideResults && (
                      // TODO: This wrapper is repeated and should be factored out into a component for reuse
                      <div className="absolute top-10 left-7 -translate-y-1/2">
                        {isTyping ? (
                          <PulseLoader color={getColors().secondary} />
                        ) : (
                          <BeatLoader color={getColors().secondary} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {highlightedOption && !shouldHideResults && (
                  <div className="flex w-1/2 flex-col border-l border-gray-100">
                    <div className="flex flex-col items-center border-b border-gray-100 p-6">
                      <ProfileCircle
                        className={cx(
                          isOptionAdded(highlightedOption) && 'bg-primary',
                        )}
                        size="lg"
                      />
                      <div className="mt-3 h-6 text-base font-semibold">
                        {highlightedOption
                          ? displayExtractor(highlightedOption)
                          : ''}
                      </div>
                    </div>
                    <div className="flex flex-col gap-6 p-6">
                      <div className="mb-2.5 flex h-5 flex-row gap-5 text-sm">
                        <div className="font-semibold">Identifier</div>
                        <div className="font-normal">
                          {highlightedOption
                            ? identifierExtractor(highlightedOption)
                            : ''}
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        disabled={
                          !highlightedOption || isOptionAdded(highlightedOption)
                        }
                        onClick={() => {
                          if (document.body) {
                            // hack to close the dropdown since there's no method to close it programmatically
                            simulateMouseClick(document.body);
                          }
                          onSelect(highlightedOption);
                        }}
                        variant="primary"
                      >
                        Select Resident
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </HuiComboboxOptions>
          </>
        );
      }}
    </HuiCombobox>
  );
};
